import { useEffect, useRef } from 'react';
import { REGIONS, EDGES, type BrainRegion } from './connectome';

interface BrainProps {
  /** region ids that fired this frame */
  firedRegions: string[];
  /** which sex's weights to visualize (edge thickness) */
  sex: 'male' | 'female';
  height?: number;
}

export function Brain({ firedRegions, sex, height = 420 }: BrainProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const firedSet = new Set(firedRegions);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = height;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    // clear
    ctx.clearRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2;
    const scale = Math.min(w, h) * 0.38;

    const pos = (r: BrainRegion) => ({
      x: cx + r.x * scale,
      y: cy + r.y * scale,
    });

    // edges
    for (const e of EDGES) {
      const from = REGIONS.find((r) => r.id === e.from);
      const to = REGIONS.find((r) => r.id === e.to);
      if (!from || !to) continue;
      const a = pos(from);
      const b = pos(to);
      const weight = sex === 'male' ? e.maleWeight : e.femaleWeight;
      const active = firedSet.has(e.from);

      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      // slight curve for aesthetics
      const mx = (a.x + b.x) / 2 + (b.y - a.y) * 0.12;
      const my = (a.y + b.y) / 2 - (b.x - a.x) * 0.12;
      ctx.quadraticCurveTo(mx, my, b.x, b.y);

      ctx.strokeStyle = active
        ? `rgba(255, 209, 102, ${0.25 + weight * 0.3})`
        : `rgba(255, 255, 255, ${0.04 + weight * 0.06})`;
      ctx.lineWidth = active ? 1.2 + weight : 0.6 + weight * 0.5;
      ctx.stroke();

      // animated pulse traveling along active edge
      if (active) {
        const t = (Date.now() / 600) % 1;
        const px = (1 - t) * (1 - t) * a.x + 2 * (1 - t) * t * mx + t * t * b.x;
        const py = (1 - t) * (1 - t) * a.y + 2 * (1 - t) * t * my + t * t * b.y;
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 209, 102, 0.9)';
        ctx.fill();
      }
    }

    // regions
    for (const r of REGIONS) {
      const p = pos(r);
      const fired = firedSet.has(r.id);
      const radius = 6 + Math.sqrt(r.neurons) / 40;

      // glow when fired
      if (fired) {
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius * 3);
        grad.addColorStop(0, 'rgba(255, 209, 102, 0.35)');
        grad.addColorStop(1, 'rgba(255, 209, 102, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius * 3, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = fired ? '#ffd166' : r.color;
      ctx.globalAlpha = fired ? 1 : 0.75;
      ctx.fill();
      ctx.globalAlpha = 1;

      // label
      ctx.font = '11px system-ui';
      ctx.fillStyle = fired ? '#ffd166' : 'rgba(255,255,255,0.55)';
      ctx.textAlign = 'center';
      ctx.fillText(r.name, p.x, p.y - radius - 6);
    }
  }, [firedRegions, sex, height, firedSet]);

  return (
    <div className="brain-wrap">
      <canvas ref={canvasRef} className="brain-canvas" style={{ height }} />
      <div className="brain-legend">
        <span className="legend-item"><span className="legend-dot" style={{ background: '#4cc9f0' }} /> sensory</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: '#b8b8ff' }} /> processing</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: '#ffd166' }} /> learning (MB)</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: '#06d6a0' }} /> motor</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: '#ff6b9d' }} /> dopaminergic</span>
        <span className="legend-item">⚡ gold = firing this frame</span>
      </div>
      <p className="brain-provenance">
        Illustrative 9-region abstraction. Region neuron counts are rounded figures from the published datasets; the inter-region
        weights are hand-invented for demonstration, not measured — and none of this graph is loaded from a connectome dataset. For real
        synapse-level data, open the sources linked in Full Brain.
      </p>
    </div>
  );
}
